import { useState } from "react";
import { cn } from "@/lib/utils";

// 요일 레이블 (일요일 = 0)
const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

// 불꽃 SVG 아이콘 — 읽은 날
const FlameIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <defs>
            <linearGradient
                id="flame-grad"
                x1="12"
                y1="2"
                x2="12"
                y2="22"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0%" stopColor="#FCD34D" />
                <stop offset="60%" stopColor="#F97316" />
                <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
        </defs>
        {/* 바깥 불꽃 */}
        <path
            d="M12 2C12 2 6.5 8.5 6.5 13.5a5.5 5.5 0 0011 0C17.5 8.5 12 2 12 2z"
            fill="url(#flame-grad)"
        />
        {/* 안쪽 코어 */}
        <path
            d="M12 9.5c0 0-2.5 3-2.5 5a2.5 2.5 0 005 0c0-2-2.5-5-2.5-5z"
            fill="white"
            fillOpacity="0.35"
        />
    </svg>
);

// 트로피 SVG 아이콘
const TrophyIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M8 21h8M12 17v4M5 3H3a1 1 0 00-1 1v2a4 4 0 004 4h.5M19 3h2a1 1 0 011 1v2a4 4 0 01-4 4h-.5M6.5 3h11v7a5.5 5.5 0 01-11 0V3z"
            stroke="#FBBF24"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

type DayDotProps = {
    date: string;
    isRead: boolean;
    isToday: boolean;
    isPast: boolean;
    index: number;
    // 읽은 날의 밝기 강도 (0~1). 최근일수록 1에 가까움
    readIntensity: number;
    onWeekendHover?: (hovering: boolean) => void;
};

const DayDot = ({
    date,
    isRead,
    isToday,
    isPast,
    index,
    readIntensity,
    onWeekendHover
}: DayDotProps) => {
    const dayOfWeek = new Date(date + "T12:00:00Z").getDay();
    const label = DAY_LABELS[dayOfWeek];
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0=일, 6=토

    // 강도에 따른 글로우 크기와 불투명도 계산
    const glowOpacity = 0.2 + readIntensity * 0.5; // 0.2 ~ 0.7
    const dotOpacity = 0.35 + readIntensity * 0.65; // 0.35 ~ 1.0

    return (
        <div
            className="flex flex-col items-center gap-1.5"
            style={{ animationDelay: `${index * 40}ms` }}
            onMouseEnter={() => isWeekend && onWeekendHover?.(true)}
            onMouseLeave={() => isWeekend && onWeekendHover?.(false)}
        >
            {/* 도트 원 */}
            <div
                className={cn(
                    `relative flex h-8 w-8 items-center justify-center
                    rounded-full`,
                    "transition-transform duration-200 ease-out",
                    // 오늘 + 아직 안 읽음: 점선 테두리 + 반투명 오렌지 tint
                    !isRead &&
                        isToday && [
                            `border border-dashed border-orange-400/70
                            bg-orange-500/10`
                        ],
                    // 오늘 + 읽음: 링 강조
                    isToday &&
                        `ring-1 ring-white/80 ring-offset-2
                        ring-offset-white dark:ring-offset-[#1a1a1a]`,
                    // 과거 + 안 읽음: 눈에 보이는 밝은 tint
                    !isRead &&
                        !isToday &&
                        isPast &&
                        "border border-black/20 dark:border-white/25 bg-orange-500/20",
                    // 미래: 어두운/밝은 배경
                    !isRead && !isToday && !isPast && "bg-gray-100 dark:bg-[#2e2e2e]"
                )}
                style={
                    isRead
                        ? {
                              // 읽은 날: 강도에 따라 그라디언트 + 글로우 opacity 변화
                              background: `linear-gradient(to bottom, rgb(251 191 36 / ${dotOpacity}), rgb(249 115 22 / ${dotOpacity}), rgb(236 72 153 / ${dotOpacity}))`,
                              boxShadow: `0 0 ${6 + readIntensity * 10}px rgba(249,115,22,${glowOpacity}), 0 2px 4px rgba(0,0,0,0.4)`
                          }
                        : undefined
                }
            >
                {isRead && (
                    <span style={{ opacity: 0.4 + readIntensity * 0.6 }}>
                        <FlameIcon />
                    </span>
                )}

                {/* 오늘이지만 아직 안 읽음 — 미니 점 */}
                {!isRead && isToday && (
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-400/80" />
                )}
            </div>

            {/* 요일 레이블 */}
            <span
                className={cn(
                    "text-[10px] tabular-nums leading-none tracking-wide",
                    isToday
                        ? "font-bold text-gray-900 dark:text-white"
                        : isWeekend
                          ? "text-orange-400/70"
                          : "text-black/30 dark:text-white/30"
                )}
            >
                {label}
            </span>

        </div>
    );
};

type DayDotsRowProps = {
    recentDays: string[];
    readDatesSet: Set<string>;
    today: string;
    className?: string;
};

/**
 * 모바일/태블릿 헤더용 DayDot 가로 줄.
 * StreakDropdown과 동일한 DayDot 컴포넌트를 재사용.
 */
export const StreakDayDotsRow = ({
    recentDays,
    readDatesSet,
    today,
    className
}: DayDotsRowProps) => {
    if (recentDays.length === 0) return null;

    // StreakDropdown과 동일한 강도 계산 로직
    const lastReadIndex = recentDays.reduce(
        (acc, date, i) => (readDatesSet.has(date) ? i : acc),
        -1
    );
    const getReadIntensity = (index: number): number => {
        if (lastReadIndex < 0) return 0.4;
        const distance = lastReadIndex - index;
        return Math.max(0.25, 1 - distance * 0.12);
    };

    return (
        <div className={cn("flex items-center gap-1", className)}>
            {recentDays.map((date, i) => (
                <DayDot
                    key={date}
                    date={date}
                    isRead={readDatesSet.has(date)}
                    isToday={date === today}
                    isPast={date < today}
                    index={i}
                    readIntensity={
                        readDatesSet.has(date) ? getReadIntensity(i) : 0
                    }
                />
            ))}
        </div>
    );
};

type Props = {
    currentStreak: number;
    longestStreak: number;
    recentDays: string[];
    readDatesSet: Set<string>;
    totalReadDays: number;
    today: string;
};

/**
 * StreakBadge 호버 시 표시되는 드롭다운.
 * - 현재/최장 스트릭
 * - 최근 9일 활동 도트
 * - 총 읽은 날 수
 * - 타임존
 */
export const StreakDropdown = ({
    currentStreak,
    longestStreak,
    recentDays,
    readDatesSet,
    totalReadDays,
    today
}: Props) => {
    const [weekendTooltipVisible, setWeekendTooltipVisible] = useState(false);

    // 읽은 날 중 가장 최근 인덱스를 기준으로 각 도트의 밝기 강도 계산
    // recentDays는 오래된 날부터 정렬 (index 0 = 가장 오래됨, last = 오늘)
    const lastReadIndex = recentDays.reduce(
        (acc, date, i) => (readDatesSet.has(date) ? i : acc),
        -1
    );

    const getReadIntensity = (index: number): number => {
        if (lastReadIndex < 0) return 0.4;
        // 최근 읽은 날(lastReadIndex)을 1.0으로, 멀수록 0.25까지 감쇠
        const distance = lastReadIndex - index;
        return Math.max(0.25, 1 - distance * 0.12);
    };

    return (
        <div
            className={cn(
                // 기본 레이아웃
                "w-[300px] rounded-2xl p-4 text-gray-900 dark:text-white",
                // 레이어드 배경 — 유리 느낌의 엣지 하이라이트 (라이트/다크 모드)
                "bg-white dark:bg-[#1a1a1a]",
                // 상단 미세 하이라이트 테두리
                "border border-black/[0.08] dark:border-white/[0.06]",
                // 레이어드 그림자 (단일 광원, 위쪽 방향)
                "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_0_0_1px_rgba(0,0,0,0.5),0_8px_32px_rgba(0,0,0,0.6),0_2px_8px_rgba(0,0,0,0.4)]"
            )}
        >
            {/* 상단 글로우 엣지 — 유리 반사 느낌 */}
            <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px
                    rounded-t-2xl"
                style={{
                    background:
                        "linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.08) 50%, transparent 90%)"
                }}
                aria-hidden="true"
            />

            {/* 현재/최장 스트릭 */}
            <div className="mb-4 flex items-start justify-between">
                {/* 현재 스트릭 */}
                <div>
                    <div className="flex items-baseline gap-1">
                        <span
                            className="text-3xl font-bold tracking-tight"
                            style={{
                                background:
                                    "linear-gradient(135deg, #FCD34D 0%, #F97316 60%, #EC4899 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            {currentStreak}
                        </span>
                        <span className="text-sm text-black/40 dark:text-white/30">
                            일
                        </span>
                    </div>
                    <div
                        className="mt-0.5 text-xs font-medium uppercase
                            tracking-wide text-black/40 dark:text-white/40"
                    >
                        현재 연속 기록
                    </div>
                </div>

                {/* 구분선 */}
                <div className="mx-3 mt-1 h-8 w-px bg-black/[0.08] dark:bg-white/[0.08]" />

                {/* 최장 스트릭 */}
                <div className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                        <TrophyIcon />
                        <span
                            className="text-3xl font-bold tracking-tight
                                text-black/80 dark:text-white/80"
                        >
                            {longestStreak}
                        </span>
                    </div>
                    <div
                        className="mt-0.5 text-xs font-medium uppercase
                            tracking-wide text-black/40 dark:text-white/40"
                    >
                        최고 기록
                    </div>
                </div>
            </div>

            {/* 최근 9일 도트 + 주말 툴팁 */}
            <div className="relative mb-4">
                <div className="flex justify-between gap-1.5">
                    {recentDays.map((date, i) => (
                        <DayDot
                            key={date}
                            date={date}
                            isRead={readDatesSet.has(date)}
                            isToday={date === today}
                            isPast={date < today}
                            index={i}
                            readIntensity={
                                readDatesSet.has(date) ? getReadIntensity(i) : 0
                            }
                            onWeekendHover={setWeekendTooltipVisible}
                        />
                    ))}
                </div>

                {/* 주말 툴팁 — 도트 행 바로 아래, 드롭다운 너비에 맞게 */}
                <div
                    className={cn(
                        "pointer-events-none absolute inset-x-0 top-full mt-2",
                        "rounded-xl px-3 py-2",
                        "bg-gray-100 dark:bg-[#1a1a1a]",
                        "border border-black/10 dark:border-white/10",
                        "text-[10px] leading-relaxed text-black/70 dark:text-white/80",
                        "shadow-xl",
                        "transition-opacity duration-150",
                        weekendTooltipVisible ? "opacity-100" : "opacity-0"
                    )}
                >
                    주말에는 콘텐츠를 읽지 않아도 불이 꺼지지 않아요.
                    <br />
                    대신 주말에는 보너스 불꽃을 획득해 보세요!
                </div>
            </div>

            {/* 푸터 — 총 읽은 날 + 타임존 */}
            <div
                className="flex items-center justify-between border-t
                    border-black/[0.06] dark:border-white/[0.06] pt-3"
            >
                <div className="text-xs text-black/40 dark:text-white/40">총 읽은 일수</div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-black/80 dark:text-white/80">
                        {totalReadDays}
                    </span>
                    <span className="text-[10px] text-black/20 dark:text-white/20">·</span>
                    <span
                        className="text-[10px] tabular-nums text-orange-400/60"
                    >
                        아시아/서울
                    </span>
                </div>
            </div>
        </div>
    );
};
